import * as systemConnectionsActions from '@Actions/systemConnections';
import {
	SystemConnections,
	SystemConnection,
	DateFormatOptions,
} from '@Types/systemConnections.types';
import {
	getSystemConnections as getSystemConnectionsService,
	updateSystemConnection as updateSystemConnectionService,
	deleteSystemConnection as deleteSystemConnService,
	getSystemConnectionById,
	createSystemConnection,
	testSystemConnectionODBC,
	testSystemConnectionOPCUA,
} from '@Services/systemConnections';
import { put, retry, takeLatest, call, delay } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';
import { error as notificationError, success } from '@innovyze/stylovyze';
import { CreateNewSystemConnectionRequest } from '@Types';
import {
	TestSystemConnectionRequestODBC,
	TestSystemConnectionRequestOPCUA,
} from '@Types/TestSystemConnectionRequest';
import { navigateTo } from '@Actions/navigate.action';
import { addRouteNamespace } from '@Utils';

function* getSystemConnectionByIdSaga(action: PayloadAction<string>) {
	const { getSystemConnectionByIdResolved, getSystemConnectionByIdRejected } =
		systemConnectionsActions;

	try {
		const response: AxiosResponse<SystemConnection> = yield call(
			getSystemConnectionById,
			action.payload,
		);

		if (response.data) {
			response.data.dateTimeFormat =
				response.data.dateTimeFormat || DateFormatOptions.ANSI.value;
			yield put(getSystemConnectionByIdResolved(response.data));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(getSystemConnectionByIdRejected());
	}
}

function* getSystemConnectionsSaga() {
	const { getSystemConnectionsResolved, getSystemConnectionsRejected } =
		systemConnectionsActions;
	try {
		const response: AxiosResponse<SystemConnections> = yield retry(
			5,
			1500,
			getSystemConnectionsService,
		);
		if (response.data) {
			yield put(getSystemConnectionsResolved(response.data));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(getSystemConnectionsRejected());
	}
}

function* createSystemConnectionSaga(
	action: PayloadAction<{ [arg: string]: string }>,
) {
	const { createSystemConnectionResolved, createSystemConnectionRejected } =
		systemConnectionsActions;

	try {
		const createNewSystemConnectionRequest =
			new CreateNewSystemConnectionRequest(
				action.payload?.connectionName,
				action.payload?.connectionType,
				action.payload?.serverType,
				action.payload?.serverAddress,
				parseInt(action.payload?.timeout),
				action.payload?.username,
				action.payload?.password,
				action.payload?.connectionString,
				action.payload?.database,
				parseInt(action.payload?.port),
				action.payload?.databaseTimezone,
				parseInt(action.payload?.sampleDurationSeconds),
				parseInt(action.payload?.queryLagMinutes),
				parseInt(action.payload?.queryGroupSize),
				action.payload?.caseSensitiveSensorId,
				parseInt(action.payload?.simulationSubtractionDays),
				action.payload?.dateTimeFormat,
				action.payload?.quoteFormat,
				action.payload?.resourcePath,
				action.payload?.opcuaUrl,
				action.payload?.timestampConversion,
				action.payload?.tagFormat,
				action.payload?.securityMode,
				action.payload?.securityPolicy,
				action.payload?.endpointMustExist,
				action.payload?.sessionName,
				action.payload?.queryPrefix,
			);
		const response: AxiosResponse<{
			systemConnectionId: string;
		}> = yield retry(
			1,
			10000,
			createSystemConnection,
			createNewSystemConnectionRequest,
		);
		if (response.data) {
			yield put(success('System Connection Successfully Created!'));
			yield put(createSystemConnectionResolved());
		} else {
			yield put(createSystemConnectionRejected());
		}
	} catch (e) {
		console.error('createSystemConnectionSaga error', e);
		yield put(notificationError('Error Creating System Connection'));
		yield put(createSystemConnectionRejected());
	}
}

function* updateSystemConnectionSaga(
	action: PayloadAction<{
		connectionId: string;
		[args: string]: string;
	}>,
) {
	const { updateSystemConnectionResolved, updateSystemConnectionRejected } =
		systemConnectionsActions;

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { connectionId, ...updateData } = action.payload;

	try {
		const updateSystemConnectionRequest =
			new CreateNewSystemConnectionRequest(
				action.payload?.connectionName,
				action.payload?.connectionType,
				action.payload?.serverType,
				action.payload?.serverAddress,
				parseInt(action.payload?.timeout),
				action.payload?.username,
				action.payload?.password,
				action.payload?.connectionString,
				// missing
				action.payload?.database,
				parseInt(action.payload?.port),
				// added for ZeroTouch
				action.payload?.databaseTimezone,
				parseInt(action.payload?.sampleDurationSeconds),
				parseInt(action.payload?.queryLagMinutes),
				parseInt(action.payload?.queryGroupSize),
				action.payload?.caseSensitiveSensorId,
				parseInt(action.payload?.simulationSubtractionDays),
				action.payload?.dateTimeFormat,
				action.payload?.quoteFormat,
				action.payload?.resourcePath,
				action.payload?.opcuaUrl,
				action.payload?.timestampConversion,
				action.payload?.tagFormat,
				action.payload?.securityMode,
				action.payload?.securityPolicy,
				action.payload?.endpointMustExist,
				action.payload?.sessionName,
				action.payload?.queryPrefix,
			);
		const response: AxiosResponse<{
			connectionId: string;
			[args: string]: string;
		}> = yield retry(
			5,
			1500,
			updateSystemConnectionService,
			connectionId,
			updateSystemConnectionRequest,
		);

		if (response.data) {
			yield put(success('System Connection Successfully Updated!'));
			yield put(updateSystemConnectionResolved(action.payload));
		} else {
			yield put(notificationError('Error Updating System Connection!'));
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error('updateSystemConnectionSaga error', e);
		yield put(notificationError('Error Updating System Connection!'));
		yield put(updateSystemConnectionRejected());
	}
}

function* deleteSystemConnectionSaga(
	action: PayloadAction<{ connectionId: string }>,
) {
	const { deleteSystemConnectionResolved, deleteSystemConnectionRejected } =
		systemConnectionsActions;
	const { connectionId: id } = action.payload;

	try {
		type Res = AxiosResponse<{ connectionId: string }>;
		const response: Res = yield retry(2, 1000, deleteSystemConnService, id);

		if (response.data) {
			yield delay(1500);
			yield put(deleteSystemConnectionResolved());
			yield put(success('System Connection Successfully Deleted!'));
			yield put(navigateTo(addRouteNamespace('system-connections')));
		} else {
			yield put(notificationError('Error Deleting System Connection!'));
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error('deleteSystemConnectionSaga error', e);
		yield put(notificationError('Error Deleting System Connection!'));
		yield put(deleteSystemConnectionRejected());
	}
}

type ConnectedResult = 'success' | 'timeout' | 'failed';

// prettier-ignore
function* testSystemConnectionSaga(//NOSONAR
	action: PayloadAction<{
		credentials: Record<string, string>;
	}>,
) {
	const {
		testSystemConnectionResolved,
		testSystemConnectionRejected,
	} = systemConnectionsActions;

	const { credentials } = action.payload;

	if (credentials.connectionString) {
		try {
			const request = new TestSystemConnectionRequestODBC(
				credentials.username,
				credentials.password,
				credentials.connectionString,
				credentials.type,
				credentials.systemConnectionId,
			);
			const response: AxiosResponse<{
				connected: ConnectedResult;
			}> = yield call(testSystemConnectionODBC, request);

			if (response.data.connected == 'success') {
				yield put(success('System Connection Successfully Tested!'));
				yield put(testSystemConnectionResolved('success'));
			} else if (response.data.connected == 'timeout') {
				yield put(
					notificationError('System Connection Test Timed Out'),
				);
				yield put(testSystemConnectionRejected('timeout'));
			} else if (response.data.connected == 'failed') {
				yield put(testSystemConnectionRejected('error'));
				throw new Error('Operation failed');
			} else {
				throw new Error(
					`Unexpected response received: ${JSON.stringify(
						response.data,
					)}`,
				);
			}
		} catch (e) {
			console.error(e);
			yield put(notificationError('Error Testing System Connection!'));
			yield put(testSystemConnectionRejected('error'));
		}
	}

	if (credentials.opcuaUrl) {
		try {
			const request = new TestSystemConnectionRequestOPCUA(
				credentials.username,
				credentials.password,
				credentials.opcuaUrl,
				credentials.sessionName,
				credentials.endpointMustExist === 'true',
				credentials.securityMode,
				credentials.securityPolicy,
				credentials.systemConnectionId,
			);
			const response: AxiosResponse<{
				connected: ConnectedResult;
			}> = yield call(testSystemConnectionOPCUA, request);
			if (response.data.connected == 'success') {
				yield put(success('System Connection Successfully Tested!'));
				yield put(testSystemConnectionResolved('success'));
			} else if (response.data.connected == 'timeout') {
				yield put(
					notificationError('System Connection Test Timed Out'),
				);
				yield put(testSystemConnectionRejected('timeout'));
			} else if (response.data.connected == 'failed') {
				yield put(testSystemConnectionRejected('error'));
				throw new Error('Operation failed');
			} else {
				throw new Error(
					`Unexpected response received: ${JSON.stringify(
						response.data,
					)}`,
				);
			}
		} catch (e) {
			console.error(e);
			yield put(notificationError('Error Testing System Connection!'));
			yield put(testSystemConnectionRejected('error'));
		}
	}
}

function* watchGetSystemConnectionById() {
	yield takeLatest(
		systemConnectionsActions.getSystemConnectionById,
		getSystemConnectionByIdSaga,
	);
}

function* watchGetSystemConnections() {
	yield takeLatest(
		systemConnectionsActions.getSystemConnections,
		getSystemConnectionsSaga,
	);
}

function* watchCreateSystemConnection() {
	yield takeLatest(
		systemConnectionsActions.createSystemConnection,
		createSystemConnectionSaga,
	);
}

function* watchUpdateSystemConnection() {
	yield takeLatest(
		systemConnectionsActions.updateSystemConnection,
		updateSystemConnectionSaga,
	);
}

function* watchDeleteSystemConnection() {
	yield takeLatest(
		systemConnectionsActions.deleteSystemConnection,
		deleteSystemConnectionSaga,
	);
}

function* watchTestSystemConnection() {
	yield takeLatest(
		systemConnectionsActions.testSystemConnection,
		testSystemConnectionSaga,
	);
}

export default [
	watchGetSystemConnectionById(),
	watchGetSystemConnections(),
	watchCreateSystemConnection(),
	watchUpdateSystemConnection(),
	watchDeleteSystemConnection(),
	watchTestSystemConnection(),
];
